import { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { User, Session } from '@supabase/supabase-js';

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  roles: string[];
  loading: boolean;
  isAdmin: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [roles, setRoles] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchRoles = useCallback(async (userId: string) => {
    // Fire-and-forget — never block onAuthStateChange.
    // Returns the promise so callers can clear `loading` only after roles resolve.
    // try/catch guarantees the promise always resolves — even on network failure —
    // so loading is cleared and AdminGuard never spins forever.
    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId);
      if (!error && data) {
        setRoles(data.map(r => r.role));
      } else {
        setRoles([]);
      }
    } catch {
      setRoles([]);
    }
  }, []);

  useEffect(() => {
    let isMounted = true;

    // 1. Restore session from storage FIRST
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (!isMounted) return;

      if (error) {
        // Corrupted/expired session — clear it so public queries work
        console.warn('Session restore failed, signing out:', error.message);
        supabase.auth.signOut();
        setSession(null);
        setUser(null);
        setRoles([]);
        setLoading(false);
        return;
      }

      setSession(session);
      setUser(session?.user ?? null);

      if (session?.user) {
        // Keep loading=true until roles resolve, so guards don't see user with roles=[]
        fetchRoles(session.user.id).then(() => {
          if (isMounted) setLoading(false);
        });
      } else {
        setLoading(false);
      }
    });

    // 2. Listen for subsequent changes (sign-in, sign-out, token refresh)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        if (!isMounted) return;

        setSession(session);
        setUser(session?.user ?? null);

        if (session?.user) {
          // Don't await — chain to avoid deadlock, but only clear loading after roles arrive
          fetchRoles(session.user.id).then(() => {
            if (isMounted) setLoading(false);
          });
        } else {
          setRoles([]);
          setLoading(false);
        }
      }
    );

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, [fetchRoles]);

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setRoles([]);
  };

  const isAdmin = roles.includes('admin');

  return (
    <AuthContext.Provider value={{ user, session, roles, loading, isAdmin, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
